import { useId } from "react";
import {
  DatePicker,
  Group,
  Label,
  DateInput as RACDateInput,
  DateSegment,
  Button,
  Dialog,
  composeRenderProps,
  type DateValue,
} from "react-aria-components";
import { Calendar as CalendarIcon } from "lucide-react";
import { tv } from "tailwind-variants";
import { KitPopover } from "@/app/routes/dashboard/components/kit/Popover";
import { cn, focusRing } from "@/utils/helpers";
import DatePickerCalendar from "./DatePickerCalendar";
import { parseIsoDate } from "./dateInputUtils";

export type DateInputVariant = "dashboard" | "field";

export interface DateInputProps {
  /** ISO date string `YYYY-MM-DD`. */
  value?: string;
  onChange?: (value: string) => void;
  label?: string;
  variant?: DateInputVariant;
  minValue?: string;
  maxValue?: string;
  isDisabled?: boolean;
  className?: string;
  id?: string;
  "aria-label"?: string;
}

const labelStyles: Record<DateInputVariant, string> = {
  dashboard: "text-[11px] uppercase tracking-[0.03em] text-co-faint",
  field:
    "mb-2 block text-[11px] font-medium uppercase tracking-[0.14em] text-co-muted",
};

const fieldGroup = tv({
  base: "co-date-field flex w-full min-w-0 items-center gap-1 transition-colors duration-200 outline-none",
  variants: {
    variant: {
      dashboard:
        "co-glass-control h-9 rounded-card px-2.5 text-[13px] text-co-text2",
      field:
        // Date field (library, style B): an Input that opens the date picker.
        "co-glass-control min-h-11 rounded-card px-3.5 py-2 text-[14px] font-medium text-co-text",
    },
    isDisabled: {
      true: "cursor-not-allowed opacity-50",
    },
    isFocusWithin: {
      true: "",
    },
  },
});

const segmentStyles = cn(
  "inline rounded-[4px] px-0.5 tabular-nums outline-none focus-visible:shadow-none caret-transparent",
  "text-inherit placeholder-shown:text-co-faint",
  "focus:bg-co-accent-dim focus:text-co-text",
  "data-placeholder:text-co-faint",
  "data-disabled:cursor-not-allowed",
);

const triggerButton = tv({
  extend: focusRing,
  base: "grid size-7 shrink-0 place-items-center rounded-full text-co-muted transition hover:bg-co-hover hover:text-co-text pressed:bg-co-hover outline-none disabled:cursor-not-allowed disabled:opacity-40",
});

export default function DateInput({
  value = "",
  onChange,
  label,
  variant = "dashboard",
  minValue,
  maxValue,
  isDisabled,
  className,
  id,
  "aria-label": ariaLabel,
}: DateInputProps) {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const parsed = parseIsoDate(value);

  const handleChange = (date: DateValue | null) => {
    onChange?.(date ? date.toString() : "");
  };

  return (
    <DatePicker
      id={inputId}
      aria-label={label ? undefined : ariaLabel ?? "Date"}
      value={parsed}
      onChange={handleChange}
      minValue={parseIsoDate(minValue) ?? undefined}
      maxValue={parseIsoDate(maxValue) ?? undefined}
      isDisabled={isDisabled}
      shouldCloseOnSelect
      className={cn("co-date-picker flex min-w-0 flex-col gap-2", className)}
    >
      {label && <Label className={cn("co-date-label", labelStyles[variant])}>{label}</Label>}
      <Group
        className={composeRenderProps("", (className, renderProps) =>
          fieldGroup({ ...renderProps, variant, className }),
        )}
      >
        <RACDateInput className="flex min-w-0 flex-1 items-center gap-0.5 px-1 py-0.5 outline-none">
          {(segment) => (
            <DateSegment segment={segment} className={segmentStyles} />
          )}
        </RACDateInput>
        <Button className={triggerButton()}>
          <CalendarIcon className="size-4" aria-hidden />
        </Button>
      </Group>

      <KitPopover
        placement="bottom start"
        offset={10}
        className="co-calendar-panel overflow-hidden p-0"
      >
        <Dialog className="outline-none">
          <DatePickerCalendar />
        </Dialog>
      </KitPopover>
    </DatePicker>
  );
}
